<template>
  <span>
    <v-row>
      <v-col md="6">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>{{ $t('return.reference') }}: {{ returnOrder.return_id }}</v-list-item-title>
            <v-list-item-subtitle class="text-uppercase" v-if="isCustomerService">
              {{ $t('return.order') }} N°
              <router-link :to="{ name: 'orderDetails', params: { id: returnOrder.order_id } }">
                {{ returnOrder.order_id }}
              </router-link>
            </v-list-item-subtitle>
            <v-list-item-subtitle
              class="text-uppercase"
              v-if="returnOrder.customer.last_name && returnOrder.customer.first_name"
            >
              {{ $t('return.client') }}: {{ returnOrder.customer.first_name }} {{ returnOrder.customer.last_name }}
            </v-list-item-subtitle>
            <v-list-item-subtitle class="text-uppercase" v-if="returnOrder.customer.email">
              {{ $t('return.clientEmail') }}: {{ returnOrder.customer.email }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="returnOrder.customer.phone" class="text-uppercase">
              {{ $t('return.clientPhone') }}: {{ returnOrder.customer.phone }}
            </v-list-item-subtitle>
            <v-list-item-subtitle class="text-uppercase">
              {{ $t('return.status') }}:
              <span text color="primary" @click="openHistoryDialog(returnOrder)">
                <v-badge color="secondary" inline icon="mdi-history">
                  {{ getStatusTranslation(returnOrder.current_status) }}
                </v-badge>
              </span>
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="returnOrder.current_status === 'cancelled' && returnOrder.cancel_reason">
              {{ $t('return.cancelReason') }}: {{ getCancelReasonTranslation(returnOrder.cancel_reason) }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-col>
      <v-col md="6" class="text-right">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-uppercase" v-if="returnOrder.date">
              {{ $t('return.date') }}: {{ new Date(returnOrder.date) | moment('DD/MM/YYYY HH:mm:ss') }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-uppercase" v-if="returnOrder?.origin_location_id">
              {{ $t('return.location') }}:
              <router-link
                v-if="canAccessStore(returnOrder?.origin_location_id)"
                :to="{ name: 'locationEdit', params: { id: returnOrder.origin_location_id } }"
              >
                {{ returnOrder.origin_location_id }}
              </router-link>
              <span v-else>
                {{ returnOrder.origin_location_id }}
              </span>
            </v-list-item-subtitle>
            <v-list-item-subtitle class="text-uppercase">
              {{ $t('return.products') }}: {{ nbProduct }}
            </v-list-item-subtitle>
            <v-list-item-subtitle class="text-uppercase">
              {{ $t('return.productsReceived') }}: {{ nbProductReceived }}
            </v-list-item-subtitle>
            <v-list-item-subtitle class="text-uppercase">
              {{ $t('return.productsRefunded') }}: {{ nbProductRefunded }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="returnOrder.return_fees_amount" class="text-uppercase">
              {{ $t('return.returnFees') }}:
              {{ returnOrder.return_fees_amount | formatPrice(returnOrder.currency) }}
              {{ returnOrder.currency.code }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="['waiting', 'waiting_products_receipt'].includes(returnOrder.current_status)">
              {{ $t('return.amount') }}: {{ returnOrder.amount | formatPrice(returnOrder.currency) }}
              {{ returnOrder.currency.code }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-else-if="returnOrder.refunded_amount !== undefined" class="text-uppercase">
              <span v-if="['products_received', 'products_partially_received'].includes(returnOrder.current_status)">
                {{ $t('return.amount') }}
              </span>
              <span v-else>{{ $t('return.refundedAmount') }}</span>
              :
              {{ returnOrder.refunded_amount | formatPrice(returnOrder.currency) }}
              {{ returnOrder.currency.code }}
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="returnOrder.current_status === 'waiting_products_receipt'">
              <span class="primary--text" v-if="returnOrder.receipt_deadline">
                {{ $t('return.returnReceiptDeadline') }}:
                {{ new Date(returnOrder.receipt_deadline) | moment('DD/MM/YYYY HH:mm:ss') }}
              </span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-col>
    </v-row>
    <!-- modals -->
    <return-status-history-dialog
      v-model="showHistoryDialog"
      @close="closeHistoryDialog()"
      :item="targetItem"
    ></return-status-history-dialog>
  </span>
</template>

<script>
  import ReturnStatusHistoryDialog from '@/components/dialogs/StatusHistoryDialog.vue'
  import { mapState } from 'vuex'

  export default {
    name: 'ReturnHeaders',
    components: {
      ReturnStatusHistoryDialog,
    },
    data() {
      return {
        showHistoryDialog: false,
        status: [
          { text: this.$t('return.waiting'), value: 'waiting' },
          { text: this.$t('return.waitingProductsReceipt'), value: 'waiting_products_receipt' },
          { text: this.$t('return.productsReceived'), value: 'products_received' },
          { text: this.$t('return.productsPartiallyReceived'), value: 'products_partially_received' },
          { text: this.$t('return.finished'), value: 'finished' },
          { text: this.$t('return.cancelled'), value: 'cancelled' },
        ],
        cancelReasons: [
          { text: this.$t('return.clientChangeOfMind'), value: 'client_changed_his_mind' },
          { text: this.$t('return.deterioratedProducts'), value: 'deteriorated_products' },
          { text: this.$t('return.missingProduct'), value: 'missing_product' },
          { text: this.$t('return.noProductApproved'), value: 'no_product_approved' },
          { text: this.$t('return.returnReceiptDeadlineExceeded'), value: 'return_receipt_deadline_exceeded' },
          { text: this.$t('return.otherReason'), value: 'other' },
        ],
        targetItem: undefined,
      }
    },
    props: {
      returnOrder: {
        type: Object,
        required: true,
      },
    },
    computed: {
      ...mapState(['isCustomerService', 'currentStore', 'isShopManager']),
      nbProduct: function () {
        let nbProduct = 0
        for (let item of this.returnOrder.items) {
          nbProduct += item.return_quantity
        }
        return nbProduct
      },
      nbProductReceived: function () {
        let nbProduct = 0
        for (let item of this.returnOrder.items) {
          if (item.returned_quantity) {
            nbProduct += item.returned_quantity
          }
        }
        return nbProduct
      },
      nbProductRefunded: function () {
        let nbProduct = 0
        for (let item of this.returnOrder.items) {
          if (item.refunded_quantity) {
            nbProduct += item.refunded_quantity
          }
        }
        return nbProduct
      },
    },
    methods: {
      openHistoryDialog: function (item) {
        this.showHistoryDialog = true
        this.targetItem = item
      },
      closeHistoryDialog: function () {
        this.showHistoryDialog = false
      },
      getStatusTranslation: function (statusValue) {
        let status = this.status.find((status) => status.value === statusValue)
        if (status) {
          return status.text
        } else return statusValue
      },
      getCancelReasonTranslation: function (cancelReasonValue) {
        let cancelReason = this.cancelReasons.find((cancelReason) => cancelReason.value === cancelReasonValue)
        if (cancelReason) {
          return cancelReason.text
        } else return cancelReasonValue
      },
      canAccessStore(locationId) {
        let isCurrentStoreManagedByUser = this.isShopManager && this.currentStore === locationId
        return this.isCustomerService || isCurrentStoreManagedByUser
      },
    },
  }
</script>
